import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, ShieldCheck, Smartphone, MousePointerClick, PartyPopper } from "lucide-react";
import MiniTimer from "@/components/landing/MiniTimer";

const steps = [
  {
    step: 1,
    icon: MousePointerClick,
    title: "\"Download the App\" ବଟନ୍ Click କରନ୍ତୁ",
    titleEn: "Click the Download Button",
    desc: "This page ରେ ଥିବା \"Download the App\" ବଟନ୍ ଉପରେ Click କରନ୍ତୁ। Google Drive ପେଜ୍ ଖୋଲିବ।",
    trust: "Google Drive Link — 100% Safe & Verified",
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    borderColor: "border-blue-100",
  },
  {
    step: 2,
    icon: Download,
    title: "APK File Download କରନ୍ତୁ",
    titleEn: "Download the APK File",
    desc: "Google Drive ରେ Download ବଟନ୍ ଦେଖିବେ। ସେଥିରେ Click କରନ୍ତୁ। File ଆପଣଙ୍କ Phone ରେ Download ହେବ।",
    trust: "File Size ମାତ୍ର ~15 MB — Slow Internet ରେ ମଧ୍ୟ Download ହୋଇଯିବ",
    color: "bg-green-50",
    iconColor: "text-green-600",
    borderColor: "border-green-100",
  },
  {
    step: 3,
    icon: ShieldCheck,
    title: "\"Install Anyway\" ଉପରେ Click କରନ୍ତୁ",
    titleEn: "Allow Installation",
    desc: "Phone ଟା Permission ପଚାରିବ — \"Install from unknown sources\" ବା \"Install Anyway\" ଉପରେ Click କରନ୍ତୁ। ଏହା Google Play ବାହାରୁ Install ପାଇଁ normal step।",
    trust: "App ସମ୍ପୂର୍ଣ୍ଣ ସୁରକ୍ଷିତ — No virus, No data theft",
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    borderColor: "border-amber-100",
  },
  {
    step: 4,
    icon: Smartphone,
    title: "App Install ହେବ — Open କରନ୍ତୁ",
    titleEn: "Install & Open the App",
    desc: "Install ହୋଇଗଲା! App Open କରନ୍ତୁ, Register କରନ୍ତୁ ଏବଂ Free tools ବ୍ୟବହାର ଆରମ୍ଭ କରନ୍ତୁ।",
    trust: "Registration FREE — ତୁରନ୍ତ Tools ବ୍ୟବହାର କରନ୍ତୁ",
    color: "bg-green-50",
    iconColor: "text-green-700",
    borderColor: "border-green-200",
  },
];

export default function InstallGuideSection({ downloadLink }) {
  return (
    <section data-testid="install-guide-section" className="py-12 md:py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-3xl">
        <h2
          data-testid="install-guide-headline"
          className="text-3xl md:text-4xl font-bold text-slate-900 text-center"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          App କେମିତି <span className="text-green-700">Download & Install</span> କରିବେ?
        </h2>
        <p className="mt-3 text-center text-slate-500 text-sm md:text-base">
          ମାତ୍ର 4 ଟି ସହଜ Step — 2 ମିନିଟ୍ ରେ App ଆପଣଙ୍କ Phone ରେ Ready!
        </p>

        <div className="mt-10 space-y-4">
          {steps.map((s, i) => (
            <Card
              key={i}
              data-testid={`install-step-${i}`}
              className={`${s.borderColor} border-2 overflow-hidden shadow-sm hover:shadow-md transition-shadow`}
            >
              <CardContent className="p-0">
                <div className="flex items-stretch">
                  {/* Step number sidebar */}
                  <div className={`${s.color} flex-shrink-0 w-16 md:w-20 flex flex-col items-center justify-center`}>
                    <span className="text-xs font-bold text-slate-400 uppercase">Step</span>
                    <span className={`text-2xl md:text-3xl font-bold ${s.iconColor}`}>{s.step}</span>
                  </div>
                  {/* Content */}
                  <div className="flex-1 p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <s.icon className={`w-5 h-5 ${s.iconColor} flex-shrink-0`} />
                      <h3
                        className="font-bold text-slate-900 text-sm md:text-base"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        {s.title}
                      </h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mt-1">{s.desc}</p>
                    <div className="mt-2 flex items-center gap-1.5 bg-green-50 rounded-md px-3 py-1.5 inline-flex">
                      <ShieldCheck className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                      <span className="text-xs font-medium text-green-700">{s.trust}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Final CTA after steps */}
        <div className="mt-8 text-center">
          <Button
            data-testid="install-guide-download-btn"
            asChild
            className="h-12 px-8 bg-green-700 hover:bg-green-800 text-white rounded-full text-base font-semibold shadow-lg shadow-green-900/20"
          >
            <a href={downloadLink} target="_blank" rel="noopener noreferrer">
              <Download className="mr-2 w-4 h-4" />
              Download the App Now
            </a>
          </Button>
          <div className="mt-3">
            <a
              href="https://teacherutilitytoolkit.in/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="install-try-online-btn"
              className="inline-flex items-center gap-2 h-11 px-6 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full text-sm font-bold shadow-lg shadow-orange-500/25 transition-all"
            >
              ⚡ Install ଦରକାର ନାହିଁ — ସିଧା ଏଠାରେ ଚଲାନ୍ତୁ!
            </a>
          </div>
          <div className="mt-2 flex justify-center">
            <MiniTimer />
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck className="w-3 h-3" />
            <span>100% Safe & Verified Download</span>
          </div>
        </div>
      </div>
    </section>
  );
}
